import * as React from "react"

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  toastInlineUpdateError,
  toastInlineUpdateSuccess,
  type InlineUpdateResult,
} from "@/lib/inline-update"

type InlineSelectOption = {
  value: string
  label: string
  testId?: string
}

type InlineSelectProps = {
  // Persisted value. Must reflect the committed change once onSubmit resolves
  // (Inertia reloads props on success, so this happens automatically).
  value: string
  options: InlineSelectOption[]
  onSubmit: (value: string) => Promise<InlineUpdateResult | void>
  successMessage?: string
  errorMessage?: string
  placeholder?: string
  id?: string
  disabled?: boolean
  size?: React.ComponentProps<typeof SelectTrigger>["size"]
  className?: string
  "data-testid"?: string
}

function InlineSelect({
  value,
  options,
  onSubmit,
  successMessage,
  errorMessage,
  placeholder,
  id,
  disabled,
  size,
  className,
  "data-testid": testId,
}: InlineSelectProps) {
  const [pendingValue, setPendingValue] = React.useState<string | null>(null)
  const [submitting, setSubmitting] = React.useState(false)

  React.useEffect(() => {
    setPendingValue((pending) => (pending === value ? null : pending))
  }, [value])

  const currentValue = pendingValue ?? value

  const handleChange = (nextValue: string) => {
    if (nextValue === currentValue || submitting) return

    setPendingValue(nextValue)
    setSubmitting(true)

    onSubmit(nextValue)
      .then((result) => {
        toastInlineUpdateSuccess(result, successMessage)
      })
      .catch((error: unknown) => {
        setPendingValue(null)
        toastInlineUpdateError(error, errorMessage)
      })
      .finally(() => setSubmitting(false))
  }

  return (
    <Select value={currentValue} onValueChange={handleChange} disabled={disabled || submitting}>
      <SelectTrigger
        id={id}
        size={size}
        className={className}
        data-slot="inline-select-trigger"
        data-testid={testId}
      >
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        {options.map((option) => (
          <SelectItem key={option.value} value={option.value} data-testid={option.testId}>
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}

export { InlineSelect, type InlineSelectOption, type InlineSelectProps }
